// import { useGroups } from '@entities/common/groups';
import { useEffect, useRef } from 'react';
import { FormProvider } from 'react-hook-form';
import { EditorAccess, EditorPartitions, EditorPeriodicity, EditorVideos } from '@widgets/programs';
import { EditorLoadPromo } from '@widgets/programs/editor-load-promo';
import { EditorSchedule } from '@widgets/programs/editor-schedule/ui.tsx';
import { FormCostForGroupsEditor } from '@widgets/shared/Forms/CostForGroupsEditor/ui.tsx';
import { FormDescriptionEditor } from '@widgets/shared/Forms/DescriptionEditor/index.tsx';
import { useEditorForm, useFormSubmit } from '@features/programs';
import { ArrowShortLeft } from '@shared/assets';
import { useMobile } from '@shared/hooks/index.ts';
import * as shared from '@shared/styles/sharedStyles.module.scss';
import {
  FormImageAndName,
  IconButton,
  ListBody,
  PageHead,
  PageTitle,
  TextButton,
} from '@shared/ui';
import { FormCommentsField } from '@shared/ui/forms/CommentsField/index.tsx';
import { FormCost } from '@shared/ui/forms/Cost/index.ts';
import { Loader } from 'library/components/common';
import * as st from './styles.module.scss';

export const ProgramEditor = () => {
  const {
    methods,
    removeExercise,
    removePromo,
    handleRedirect,
    clearStorage,
    programId,
    code,
    id,
    isSubmitDisabled,
    isProgramLoading,
    defaultData,
    register,
    onClose,
    errors,
    _HASH,
  } = useEditorForm();

  const { isMobile } = useMobile();
  const { onProgramSubmit, uploadStatus, progress } = useFormSubmit({
    programId,
    code,
    onSuccess: clearStorage,
  });

  const moduleRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const errorKeys = Object.keys(errors);
    if (errorKeys.length) {
      const scrollOptions = { behavior: 'smooth', block: 'center' } as ScrollIntoViewOptions;
      const firstError = errorKeys[0];
      if (firstError === 'promo_image') {
        document.getElementById('imageCropper')?.scrollIntoView(scrollOptions);
      }
      if (firstError === 'title') {
        document.getElementsByTagName('textarea')[isMobile ? 1 : 0]?.scrollIntoView(scrollOptions);
      }
      if (firstError === 'module') {
        moduleRef?.current?.scrollIntoView(scrollOptions);
      }
    }
  }, [errors, isMobile]);

  return (
    <>
      <PageTitle title={id === 'new' ? 'Новая видеопрограмма' : 'Редактирование программы'} />
      <PageHead
        Left={
          <div className={st.button}>
            <IconButton variant="dark" onClick={onClose}>
              <ArrowShortLeft />
            </IconButton>
          </div>
        }
        Right={
          <TextButton
            type="submit"
            disabled={isSubmitDisabled}
            onClick={methods.handleSubmit(onProgramSubmit)}
          >
            Готово
          </TextButton>
        }
      />

      <ListBody>
        {!isProgramLoading && uploadStatus === 'idle' ? (
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onProgramSubmit)} className={shared.forFormsGap20}>
              {/*<EditorTitle />*/}

              <FormImageAndName
                imageError={methods.formState.errors.promo_image?.message}
                titleError={methods.formState.errors.title?.message}
                imageValue={defaultData?.promo_image}
                titleValue={defaultData?.title || ''}
                onImageChange={(photo) => {
                  methods.clearErrors('promo_image');
                  methods.setValue('promo_image', photo, { shouldDirty: photo !== '' });
                }}
                onTitleChange={(title) => {
                  methods.clearErrors('title');
                  methods.setValue('title', title, { shouldDirty: title !== '' });
                }}
                placeholder="Название"
                required
              />

              {/*<FormFieldWithTextArea*/}
              {/*  fieldLabel={'описание'}*/}
              {/*  // error={errors?.description}*/}
              {/*  register={register('description')}*/}
              {/*/>*/}

              <FormDescriptionEditor
                hash={_HASH}
                onChange={(v) => methods.setValue('description', v)}
                onRedirect={handleRedirect}
                initData={defaultData?.description}
              />

              <EditorLoadPromo
                onRedirect={handleRedirect}
                promoId={methods.getValues('promo_video')}
                onPromoChange={(promoId) => methods.setValue('promo_video', promoId)}
                onRemove={removePromo}
              />

              <EditorPartitions onRedirect={handleRedirect} hash={_HASH} />

              <EditorPeriodicity />
              <EditorSchedule />

              <EditorVideos
                moduleRef={moduleRef}
                initVideosIds={defaultData?.exercises}
                onVideosIdsChange={(ids) =>
                  methods.setValue('exercises', ids, { shouldDirty: true })
                }
                onRedirect={handleRedirect}
                editorFormData={{ methods, removeExercise }}
              />

              <FormCost
                // error={errors?.cost}
                register={register('cost_base')}
                placeholder={'0'}
                // min={0}
              />
              {/*<EditorBaseCost />*/}

              <FormCostForGroupsEditor
                nameOfArray={'costs'}
                onRedirect={handleRedirect}
                formContextFromProps={methods}
                initData={defaultData?.costs}
              />
              {/*<EditorCostForGroups onRedirect={handleClick} />*/}

              <EditorAccess />

              <FormCommentsField
                initData={defaultData?.with_comments}
                onChange={(isIncluded) =>
                  methods.setValue('with_comments', isIncluded, { shouldDirty: true })
                }
              />
            </form>

            {/*<SearchParams />*/}
          </FormProvider>
        ) : (
          <Loader text="Создание программы. Не закрывайте данную страницу до завершения процесса" />
        )}
      </ListBody>

      {/*{uploadStatus === 'loading' && (*/}
      {/*  <UploadProgress*/}
      {/*    progress={progress}*/}
      {/*    title={id === 'new' ? 'Создаем видеопрограмму...' : 'Обновляем видеопрограмму...'}*/}
      {/*  />*/}
      {/*)}*/}
    </>
  );
};
